<script>
  import { toggleZoom, debounce, findVariantImageIndex, buildSizedImageUrl, buildResponsiveSrcset, buildPdpMobileSlideSrc, handleViewportSize, getMediaAlt } from '@/lib/vue-helpers';
  import PDPVideoPlayer from '@/components/PDPVideoPlayer.vue';
  import PDPFreescroll from '@/components/PDPFreescroll.vue';
  import { emitter } from '@/main.js';
  export default {
    name: 'PDPImages',
    props: {
      images: {
        type: Array,
        required: true
      },
      media: Array
    },
    components: {
      PDPVideoPlayer,
      PDPFreescroll
    },
    data() {
      return {
        dataZoomEnable: false,
        pdp_items_mobile: 1,
        show_pagination: false,
        show_arrows: false,
        arrow_left_image: '',
        arrow_left_image_alt: '',
        arrow_left_image_width: '',
        arrow_left_image_height: '',
        arrow_left_image_disabled: '',
        arrow_left_image_disabled_alt: '',
        arrow_left_image_disabled_width: '',
        arrow_left_image_disabled_height: '',
        arrow_custom_on: false,
        carouselKey: 0,
        currentSlide: 0,
        mini_pdp: false,
        windowWidth: window.innerWidth,
        localImages: [],
        localMedia: this.media || [],
        pdp_desktop_image_width: window.pdp_desktop_image_width,
        pdp_mobile_image_width: window.pdp_mobile_image_width,
        pdp_mobile_max_height: window.pdp_mobile_max_height || 600,
        video_height_grow: true,
        upsellImages: [],
        zoom_gutter_mobile: 5,
        showAllImages: false,
        desktopImageLimit: 4,
        tabletImageLimit: 2,
        mobileImageLimit: 2,
        desktopRowNumber: 2,
        tabletRowNumber: 1,
        expandText: 'Show More Images',
        collapseText: 'Show Less Images',
        expandCollapseBtnLineOffset: 0,
        expandCollapseBtnMarginTop: 26,
        desktopPreloadCount: 4,
        mobilePreloadCount: 1,
        miniPdpResetRetryCounter: 0,
        teleportTarget: null,
        previousImages: [],
        previousMedia: [],
        newImagesReady: true,
        imageTransitionTimeout: null
      };
    },
    computed: {
      shouldNotifySingleImageFreescrollReady() {
        if (!this.teleportTarget) return false;
        if (this.localMedia.length !== 1) return false;
        return this.isMobile || this.mini_pdp === 'true';
      },
      isMobile() {
        return this.windowWidth < window.theme.sizes.medium;
      },
      isTablet() {
        return !this.isMobile && this.windowWidth < 1100;
      },
      mediaToDisplay() {
        // On mobile, show previous media during transition to prevent blank space
        let media = this.localMedia;
        if (this.isMobile && this.mini_pdp === 'true') {
          media = this.localMedia.slice(0, 3);
        }

        // If new images aren't ready yet, use previous media to prevent blank screen
        if ((this.isMobile || this.mini_pdp === 'true') && !this.newImagesReady && this.previousMedia.length > 0) {
          return this.mini_pdp === 'true' ? this.previousMedia.slice(0, 3) : this.previousMedia;
        }

        return media;
      },
      shouldShowArrows() {
        if (this.mini_pdp === 'true' && this.isMobile) {
          return false;
        }
        return this.show_arrows === 'true';
      },
      getCurrentImageLimit() {
        if (this.isMobile) {
          return this.mobileImageLimit;
        } else if (this.isTablet) {
          return this.tabletImageLimit;
        }
        return this.desktopImageLimit;
      },
      shouldShowExpandButton() {
        if (this.isMobile) {
          return false;
        }
        return this.mediaToDisplay.length > this.getCurrentImageLimit;
      },
      getAllImagesWithToggle() {
        if (this.isMobile || this.mini_pdp) {
          return this.mediaToDisplay;
        }
        const images = this.showAllImages ? this.mediaToDisplay : this.mediaToDisplay.slice(0, this.getCurrentImageLimit);
        return images;
      }
    },
    created() {
      this.debouncedResize = debounce(
        () => {
          const previousIsMobile = this.isMobile;
          const previousWindowWidth = this.windowWidth;
          this.windowWidth = window.innerWidth;
          if (window.setClientWidthClass) {
            window.setClientWidthClass();
          }
          const currentIsMobile = this.windowWidth < window.theme.sizes.medium;
          const deviceTypeChanged = previousIsMobile !== currentIsMobile;
          if (deviceTypeChanged || Math.abs(previousWindowWidth - this.windowWidth) > 100) {
            this.reinitializeComponents(currentIsMobile, deviceTypeChanged);
          }
          this.$nextTick(() => {
            this.initPdpScrollSync();
            this.schedulePdpScrollSyncMeasure();
          });
        },
        150,
        false
      );
      this.localImages = [...this.images];

      // Preload critical first images immediately for faster perceived load
      this.preloadCriticalImages();
    },
    mounted() {
      // Preload critical first images immediately for faster perceived load
      this.preloadCriticalImages();
      this.handleViewportSize = handleViewportSize(this);
      if (window.setClientWidthClass) {
        window.setClientWidthClass();
      }
      const parentEl = this.$el.parentElement;
      this.dataZoomEnable = parentEl.getAttribute('data-zoomEnable');
      this.pdp_items_mobile = parseFloat(parentEl.getAttribute('data-pdp_items_mobile'));
      this.arrow_custom_on = parentEl.getAttribute('data-arrow_custom_on');
      this.arrow_left_image = parentEl.getAttribute('data-arrow_left_image');
      this.arrow_left_image_alt = parentEl.getAttribute('data-arrow_left_image_alt');
      this.arrow_left_image_width = parentEl.getAttribute('data-arrow_left_image_width');
      this.arrow_left_image_height = parentEl.getAttribute('data-arrow_left_image_height');
      this.arrow_left_image_disabled = parentEl.getAttribute('data-arrow_left_image_disabled');
      this.arrow_left_image_disabled_alt = parentEl.getAttribute('data-arrow_left_image_disabled_alt');
      this.arrow_left_image_disabled_width = parentEl.getAttribute('data-arrow_left_image_disabled_width');
      this.arrow_left_image_disabled_height = parentEl.getAttribute('data-arrow_left_image_disabled_height');
      this.show_arrows = parentEl.getAttribute('data-show_arrows');
      this.show_pagination = parentEl.getAttribute('data-show_pagination');
      this.mini_pdp = parentEl.dataset.miniPdp;
      this.pdp_mobile_max_height = parentEl.getAttribute('data-mobile_max_height');
      this.video_height_grow = parentEl.getAttribute('data-video_height_grow');
      this.zoom_gutter_mobile = parentEl.getAttribute('data-zoom_gutter_mobile') || 5;

      this.handleReplaceVariantImage = e => {
        // On mobile, keep old images until new ones are loaded
        if (this.isMobile || this.mini_pdp === 'true') {
          this.transitionToNewImages(e, e);
          return;
        }

        // Desktop: update immediately
        this.localImages = e;
        this.localMedia = e;
        this.carouselKey++;
        this.$nextTick(() => {
          this.preloadCriticalImages();
          this.updateExpandedLayout();
        });
      };

      const gridDesktopLimit = parentEl.getAttribute('data-gallery-desktop-limit');
      const gridTabletLimit = parentEl.getAttribute('data-gallery-tablet-limit');
      const gridMobileLimit = parentEl.getAttribute('data-gallery-mobile-limit');
      const gridDesktopRowNumber = parentEl.getAttribute('data-gallery-desktop-row-number');
      const gridTabletRowNumber = parentEl.getAttribute('data-gallery-tablet-row-number');
      const gridExpandText = parentEl.getAttribute('data-gallery-expand-text');
      const gridCollapseText = parentEl.getAttribute('data-gallery-collapse-text');
      const gridExpandCollapseBtnLineOffset = parentEl.getAttribute('data-gallery-expand-collapse-btn-line-offset');
      const gridExpandCollapseBtnMarginTop = parentEl.getAttribute('data-gallery-expand-collapse-margin-top');
      if (gridDesktopLimit) this.desktopImageLimit = parseInt(gridDesktopLimit);
      if (gridTabletLimit) this.tabletImageLimit = parseInt(gridTabletLimit);
      if (gridMobileLimit) this.mobileImageLimit = parseInt(gridMobileLimit);
      if (gridDesktopRowNumber) this.desktopRowNumber = parseInt(gridDesktopRowNumber);
      if (gridTabletRowNumber) this.tabletRowNumber = parseInt(gridTabletRowNumber);
      if (gridExpandText) this.expandText = gridExpandText;
      if (gridCollapseText) this.collapseText = gridCollapseText;
      if (gridExpandCollapseBtnLineOffset) this.expandCollapseBtnLineOffset = gridExpandCollapseBtnLineOffset;
      if (gridExpandCollapseBtnMarginTop) this.expandCollapseBtnMarginTop = gridExpandCollapseBtnMarginTop;

      const freescrollTarget = document.getElementById('pdp-freescroll-target');
      if (freescrollTarget) {
        this.teleportTarget = '#pdp-freescroll-target';
        freescrollTarget.classList.remove('pdp-teleport-ready');
      }

      this.handleUpsellImagesUpdated = e => {
        // Remove any existing upsell images from the arrays
        this.localImages = this.localImages.filter(image => !this.upsellImages.includes(image));
        this.localMedia = this.localMedia.filter(media => !this.upsellImages.includes(media));

        // add new images to the upsell images array
        const newImageOne = e[0];
        const newImageTwo = e[1];
        this.upsellImages.push(newImageOne);
        this.upsellImages.push(newImageTwo);

        // make sure 2nd image is 2nd in the array
        if (newImageTwo) {
          this.localImages.unshift(newImageTwo);
          this.localMedia.unshift(newImageTwo);
        }

        // make sure 1st image is 1st in the array
        if (newImageOne) {
          this.localImages.unshift(newImageOne);
          this.localMedia.unshift(newImageOne);
        }

        this.$nextTick(() => this.updateExpandedLayout());

        this.variantRetryCounter = 0;
        if (this.ispdpCarouselAvailable()) {
          this.$refs.pdpCarousel.slideTo(0);
        }
      };

      emitter.on('replace-variant-image', this.handleReplaceVariantImage);
      emitter.on('upsell-images-updated', this.handleUpsellImagesUpdated);
      emitter.on('pdp-color-updated', this.handleColorUpdate);
      this.handleMiniPdpVariantUpdated = () => {
        if (this.mini_pdp === 'true') {
          try {
            const tryReset = (remaining = 5) => {
              const freescroll = this.$refs.pdpFreescroll;
              const instanceReady = freescroll && freescroll.flickityInstance;
              if (instanceReady) {
                if ((freescroll.currentSlide ?? 0) !== 0) {
                  freescroll.goToSlide(0);
                }
                return;
              }
              if (remaining > 0) {
                setTimeout(() => tryReset(remaining - 1), 60);
              } else {
                this.carouselKey++;
              }
            };
            tryReset(5);
          } catch (e) {
            this.carouselKey++;
          }
        }
      };
      emitter.on('variant-updated', this.handleMiniPdpVariantUpdated);

      document.addEventListener('theme:resize:width', this.debouncedResize);

      this.updateExpandedLayout();
      this.$nextTick(() => {
        this.tryNotifySingleImageFreescrollReady();
        this.initPdpScrollSync();
      });
    },
    watch: {
      shouldNotifySingleImageFreescrollReady(val) {
        if (val) {
          this.$nextTick(() => this.tryNotifySingleImageFreescrollReady());
        }
      }
    },
    methods: {
      getMediaAlt,
      handleColorUpdate(e) {
        const el = document.getElementById('PDPImages');
        if (el) {
          if (this.isMobile) {
            el.style.minHeight = `${el.offsetHeight}px`;
          } else {
            el.style.minHeight = '';
          }
        }

        this.upsellImages = [];
        this.variantRetryCounter = 0;
        this.currentSlide = 0;

        // On mobile, keep old images until new ones are loaded
        if (this.isMobile || this.mini_pdp === 'true') {
          this.transitionToNewImages(e.images, e.media, this.mobilePreloadCount, 2000);
        } else {
          this.transitionToNewImages(e.images, e.media, this.desktopPreloadCount, 1000);
        }
      },
      handleVariantUpdate(e) {
        if (this.isMobile || this.mini_pdp === 'true') {
          return;
        }
        const variantImage = e.image;
        const variantWidth = e.width;
        const currentSlideIndex = this.currentSlide || 0;
        const currentImage = this.localImages[currentSlideIndex];
        const currentImageUrl = currentImage?.node?.url;
        if (currentImageUrl === variantImage) {
          return;
        }
        const imageIndex = findVariantImageIndex(this.localImages, variantImage, variantWidth);
        if (imageIndex !== -1) {
          this.updateSlideByVariant(imageIndex);
        }
      },
      getDesktopImageHeights() {
        const useMin = this.video_height_grow === 'false';
        const root = document.getElementById('PDPImages');
        if (!root) return;
        const videoContainers = root.querySelectorAll('.product__media.desktop-images.desktop-video-height');
        if (!videoContainers.length) {
          this.getMobileImageHeights();
          return;
        }
        const imgEls = root.querySelectorAll('.product__media.desktop-images:not(.desktop-video-height) img');
        const ratios = [];
        imgEls.forEach(img => {
          if (img.naturalWidth && img.naturalHeight) {
            ratios.push(img.naturalHeight / img.naturalWidth);
          }
        });
        const fallbackRatio = ratios.length ? (useMin ? Math.min(...ratios) : Math.max(...ratios)) : null;
        videoContainers.forEach(videoContainer => {
          const videoEl = videoContainer.querySelector('video');
          let ratio = videoEl && videoEl.videoWidth && videoEl.videoHeight ? videoEl.videoHeight / videoEl.videoWidth : fallbackRatio;
          if (ratio) {
            videoContainer.style.height = Math.round(videoContainer.clientWidth * ratio) + 'px';
          }
        });
        this.getMobileImageHeights();
      },
      getMobileImageHeights() {
        let mobileHeights = this.pdp_mobile_max_height;
        const root = this.teleportTarget ? document.querySelector(this.teleportTarget) : document.getElementById('PDPImages');
        if (!root) return;
        const images = root.querySelectorAll('.pdp-freescroll .freescroll__slide img.slideShow');
        const hasLoadedImage = Array.from(images).some(img => img.clientHeight > 0);
        if (!hasLoadedImage) return;
        images.forEach(image => {
          if (image.clientHeight < mobileHeights && image.clientHeight > 0) {
            mobileHeights = image.clientHeight;
          }
        });
        const videos = root.querySelectorAll('.pdp-freescroll .pdp-video-player');
        videos.forEach(video => {
          video.style.height = mobileHeights + 'px';
        });
      },
      updateSlideByVariant(index) {
        try {
          if (this.$refs.pdpCarousel && typeof this.$refs.pdpCarousel.slideTo === 'function') {
            this.$refs.pdpCarousel.slideTo(index);
            this.currentSlide = index;
          } else {
            if (!this.isMobile && this.mini_pdp !== 'true') {
              this.scrollToDesktopImage(index);
            }
          }
        } catch (e) {
          setTimeout(() => {
            this.variantRetryCounter = this.variantRetryCounter + 1;
            if (this.variantRetryCounter < 5) {
              this.updateSlideByVariant(index);
            }
          }, 200);
        }
      },
      scrollToDesktopImage(index) {
        try {
          const desktopImages = document.querySelectorAll('.product__media.desktop-images');

          if (desktopImages[index]) {
            desktopImages[index].scrollIntoView({ behavior: 'smooth', block: 'center' });
            this.currentSlide = index;
          }
        } catch (error) {
          // Silently handle errors in desktop scrolling
        }
      },
      setImageDesktopSize(imgUrl) {
        return buildSizedImageUrl(imgUrl, this.pdp_desktop_image_width);
      },
      setImageMobileSize(imgUrl) {
        return buildPdpMobileSlideSrc(imgUrl, this.pdp_mobile_image_width);
      },
      getDesktopImageSrcset(imgUrl) {
        return buildResponsiveSrcset(imgUrl, this.pdp_desktop_image_width);
      },
      getMobileImageSrcset(imgUrl) {
        return buildResponsiveSrcset(imgUrl, this.pdp_mobile_image_width);
      },
      getImageUrl(imgUrl) {
        return this.isMobile ? this.setImageMobileSize(imgUrl) : this.setImageDesktopSize(imgUrl);
      },
      getMobileSingleSlideImageUrl(product) {
        return product?.node?.image?.url || product?.node?.url || '';
      },
      slidePrev() {
        const carousel = this.$refs.pdpCarousel;
        carousel.prev();
      },
      slideNext() {
        const carousel = this.$refs.pdpCarousel;
        carousel.next();
      },
      handleCarouselSlideEnd() {
        emitter.emit('carousel-slide-end');
      },
      notifyFreescrollTeleportReady() {
        const el = document.getElementById('PDPImages');
        if (el) el.classList.remove('mobile-height-lock');
        const mobileCarousel = document.querySelector('.product-page .mobile-carousel-block');
        if (mobileCarousel) mobileCarousel.style.minHeight = '';
        const freescrollTarget = this.teleportTarget ? document.querySelector(this.teleportTarget) : null;
        if (freescrollTarget) freescrollTarget.classList.add('pdp-teleport-ready');
        this.$nextTick(() => {
          emitter.emit('carousel-ready');
        });
      },
      tryNotifySingleImageFreescrollReady() {
        if (!this.shouldNotifySingleImageFreescrollReady) return;
        this.notifyFreescrollTeleportReady();
      },
      handleCarouselInit() {
        this.notifyFreescrollTeleportReady();
      },
      handleZoom(productImageUrl) {
        emitter.emit('active-product-slide-for-zoom', productImageUrl);
        toggleZoom();
      },
      toggleImageExpansion() {
        this.showAllImages = !this.showAllImages;
        this.updateExpandedLayout();
        this.$nextTick(() => this.schedulePdpScrollSyncMeasure());
        if (!this.showAllImages) {
          this.scrollToTop();
        }
      },
      scrollToTop() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
      reinitializeComponents(isMobile, deviceTypeChanged) {
        this.$nextTick(() => {
          try {
            if (!isMobile) {
              this.getDesktopImageHeights();
            } else {
              this.getMobileImageHeights();
            }
            if (deviceTypeChanged && this.$refs.pdpFreescroll) {
              this.carouselKey++;
            }
            if (deviceTypeChanged) {
              emitter.emit('device-type-changed', { isMobile });
            }
            this.updateExpandedLayout();
          } catch (error) {
            console.warn('Error reinitializing components on resize:', error);
          }
        });
      },
      updateExpandedLayout() {
        const el = document.getElementById('PDPImages');
        if (!el) return;
        // Ensure no horizontal overflow on single-image mobile view
        if (this.isMobile && this.localImages && this.localImages.length === 1) {
          el.classList.add('pdp-single-image-mobile');
        } else {
          el.classList.remove('pdp-single-image-mobile');
        }
        // Apply grid override only when layout is columns or mosaic; for 'single', allow full width stacking
        if (!this.isMobile) {
          const baseRow = this.isTablet ? this.tabletRowNumber : this.desktopRowNumber;
          const limit = this.isTablet ? this.tabletImageLimit : this.desktopImageLimit;
          const visibleCount = this.getAllImagesWithToggle.length;
          const columns = Math.max(1, Math.min(baseRow, limit, visibleCount));
          const isMosaic = el.classList.contains('product__grid--mosaic');
          const isColumns = el.classList.contains('product__grid--columns');
          if (isMosaic || isColumns) {
            el.classList.add('pdp-gallery-grid-override');
            el.style.setProperty('--PDP-GALLERY-COLUMNS', columns);
          } else {
            // 'single' layout
            el.classList.remove('pdp-gallery-grid-override');
            el.style.removeProperty('--PDP-GALLERY-COLUMNS');
          }
          // Expanded state only toggles visibility of hidden items; keep class for potential styling hooks
          if (this.showAllImages) {
            el.classList.add('pdp-gallery-expanded');
          } else {
            el.classList.remove('pdp-gallery-expanded');
          }
          el.style.minHeight = '';
        } else {
          el.classList.remove('pdp-gallery-grid-override');
          el.classList.remove('pdp-gallery-expanded');
          el.style.removeProperty('--PDP-GALLERY-COLUMNS');
        }
      },
      transitionToNewImages(newImages, newMedia, preloadCount = 1, fallbackDelay = 2000) {
        // Store current images as previous for transition
        if (this.localImages.length > 0 && !this.newImagesReady) {
          this.previousImages = [...this.localImages];
          this.previousMedia = [...this.localMedia];
        }

        // Set new images but keep showing old ones until loaded
        const stagingImages = [...newImages];
        const stagingMedia = [...newMedia];

        // Preload new images first
        this.preloadImagesAndSwap(stagingImages, stagingMedia, preloadCount);

        // Fallback: if images don't load within the delay, show new images anyway
        if (this.imageTransitionTimeout) {
          clearTimeout(this.imageTransitionTimeout);
        }
        this.imageTransitionTimeout = setTimeout(() => {
          if (!this.newImagesReady && stagingImages.length > 0) {
            this.completeImageTransition(stagingImages, stagingMedia);
          }
        }, fallbackDelay);
      },

      getPreloadImageUrls(newMedia, newImages, preloadCount) {
        const medium = window.theme?.sizes?.medium;
        const isMobileDevice = typeof medium === 'number' ? window.innerWidth <= medium : false;
        const mediaToUse = Array.isArray(newMedia) && newMedia.length > 0 ? newMedia : newImages;
        const imageUrls = [];
        mediaToUse.forEach(item => {
          if (imageUrls.length >= preloadCount) return;
          const imageUrl = item?.node?.image?.url || item?.node?.url;
          if (!imageUrl) return;
          const sizedUrl = isMobileDevice ? this.setImageMobileSize(imageUrl) : this.setImageDesktopSize(imageUrl);
          imageUrls.push(sizedUrl);
        });
        return imageUrls;
      },

      preloadImagesAndSwap(newImages, newMedia, preloadCount = 1) {
        this.newImagesReady = false;
        const imageUrls = this.getPreloadImageUrls(newMedia, newImages, preloadCount);
        if (imageUrls.length === 0) {
          this.completeImageTransition(newImages, newMedia);
          return;
        }

        let loadedCount = 0;
        const finalize = () => {
          if (this.newImagesReady) return;
          this.completeImageTransition(newImages, newMedia);
        };

        imageUrls.forEach(url => {
          const img = new Image();
          img.onload = () => {
            loadedCount += 1;
            if (loadedCount >= imageUrls.length) {
              finalize();
            }
          };
          img.onerror = () => {
            loadedCount += 1;
            if (loadedCount >= imageUrls.length) {
              finalize();
            }
          };
          img.src = url;
        });
      },

      completeImageTransition(newImages, newMedia) {
        if (this.imageTransitionTimeout) {
          clearTimeout(this.imageTransitionTimeout);
          this.imageTransitionTimeout = null;
        }

        this.localImages = newImages;
        this.localMedia = newMedia;
        this.newImagesReady = true;
        this.carouselKey++;

        // Clear previous images after transition
        this.$nextTick(() => {
          this.previousImages = [];
          this.previousMedia = [];
          this.preloadCriticalImages();
          this.updateExpandedLayout();
          this.schedulePdpScrollSyncMeasure();
          this.tryNotifySingleImageFreescrollReady();
        });
      },

      preloadCriticalImages() {
        const medium = window.theme?.sizes?.medium;
        const isMobileDevice = typeof medium === 'number' ? window.innerWidth <= medium : false;

        // Preload first image immediately for faster LCP
        if (this.localImages && this.localImages.length > 0) {
          const firstImage = this.localImages[0];
          const firstImageUrl = firstImage?.node?.image?.url || firstImage?.node?.url;
          if (firstImageUrl) {
            const imgUrl = isMobileDevice ? this.setImageMobileSize(firstImageUrl) : this.setImageDesktopSize(firstImageUrl);

            const existingPreload = document.querySelector(`link[rel="preload"][as="image"][href="${imgUrl}"]`);
            if (!existingPreload) {
              const link = document.createElement('link');
              link.rel = 'preload';
              link.as = 'image';
              link.href = imgUrl;
              link.fetchPriority = 'high';
              document.head.appendChild(link);
            }
          }
        }

        // Preload next few images in idle time (non-blocking)
        if ('requestIdleCallback' in window && this.localImages.length > 1) {
          requestIdleCallback(
            () => {
              const preloadCount = Math.min(3, this.localImages.length);
              const themeMedium = window.theme?.sizes?.medium;
              const isMobile = typeof themeMedium === 'number' ? window.innerWidth <= themeMedium : false;
              for (let i = 1; i < preloadCount; i++) {
                const image = this.localImages[i];
                if (image?.node?.image?.url || image?.node?.url) {
                  const imgUrl = image.node.image?.url || image.node.url;
                  const sizedUrl = isMobile ? this.setImageMobileSize(imgUrl) : this.setImageDesktopSize(imgUrl);

                  const link = document.createElement('link');
                  link.rel = 'preload';
                  link.as = 'image';
                  link.href = sizedUrl;
                  link.fetchPriority = 'low';
                  document.head.appendChild(link);
                }
              }
            },
            { timeout: 2000 }
          );
        }
      },

      handleSlideChange(payload) {
        let index = 0;
        if (typeof payload === 'number') {
          index = payload;
        } else if (typeof payload.index === 'number') {
          index = payload.index;
        }
        this.currentSlide = index;
      },
      initPdpScrollSync() {
        if (this.mini_pdp === 'true') return;
        if (typeof ResizeObserver === 'undefined') return;

        // Must track the sticky media query in sections/product.liquid, which keys off layout_desktop_min_width - a
        // setting separate from theme.sizes.large. Bail instead of guessing: any fallback width would flag columns
        // ready at viewports the CSS never makes sticky.
        const desktopMin = Number(window.layoutDesktopMinWidth);
        if (!Number.isFinite(desktopMin) || desktopMin <= 0) {
          this.teardownPdpScrollSync();
          return;
        }

        const isDesktop = window.matchMedia(`(min-width: ${desktopMin}px)`).matches;
        if (!isDesktop) {
          this.teardownPdpScrollSync();
          return;
        }
        if (this._pdpScrollSyncObserver) return;

        const gallery = this.$el?.parentElement;
        if (!gallery) return;

        const mediaColumn = gallery.closest('.product__media__wrapper');
        if (!mediaColumn) return;

        const productSingle = mediaColumn.closest('.product-single');
        if (!productSingle) return;

        // thumbnails-left drops align-items to flex-start on the row, so neither column stretches to the row height
        // and sticky has no travel. Bail rather than flag the columns ready for a sync that cannot happen.
        if (productSingle.hasAttribute('data-thumbnails-left')) return;

        const detailInner = productSingle.querySelector('.product__main__content > .product__inner');
        if (!detailInner) return;

        this._pdpScrollSyncProductSingle = productSingle;
        this._pdpScrollSyncTargets = [mediaColumn, detailInner];
        this.measurePdpScrollSyncHeights();
        // Third-party PDP content and media can resize independently of component updates or viewport changes.
        this._pdpScrollSyncObserver = new ResizeObserver(() => {
          this.schedulePdpScrollSyncMeasure();
        });
        this._pdpScrollSyncTargets.forEach(el => this._pdpScrollSyncObserver.observe(el));
      },
      schedulePdpScrollSyncMeasure() {
        if (this._pdpScrollSyncRaf) return;
        this._pdpScrollSyncRaf = requestAnimationFrame(() => {
          this._pdpScrollSyncRaf = null;
          this.measurePdpScrollSyncHeights();
        });
      },
      measurePdpScrollSyncHeights() {
        if (!this._pdpScrollSyncTargets) return;

        let ready = true;
        this._pdpScrollSyncTargets.forEach(el => {
          const height = el.getBoundingClientRect().height;
          if (height <= 0) {
            ready = false;
            return;
          }
          el.style.setProperty('--pdp-scroll-sync-height', `${height}px`);
        });

        if (!this._pdpScrollSyncProductSingle) return;
        this._pdpScrollSyncProductSingle.classList.toggle('pdp-scroll-sync-ready', ready);
      },
      teardownPdpScrollSync() {
        if (this._pdpScrollSyncRaf) {
          cancelAnimationFrame(this._pdpScrollSyncRaf);
          this._pdpScrollSyncRaf = null;
        }
        if (this._pdpScrollSyncObserver) {
          this._pdpScrollSyncObserver.disconnect();
          this._pdpScrollSyncObserver = null;
        }
        if (this._pdpScrollSyncTargets) {
          this._pdpScrollSyncTargets.forEach(el => {
            el.style.removeProperty('--pdp-scroll-sync-height');
          });
          this._pdpScrollSyncTargets = null;
        }
        if (this._pdpScrollSyncProductSingle) {
          this._pdpScrollSyncProductSingle.classList.remove('pdp-scroll-sync-ready');
          this._pdpScrollSyncProductSingle = null;
        }
      }
    },
    beforeUnmount() {
      this.debouncedResize.cancel();
      this.teardownPdpScrollSync();
      if (this.handleViewportSize) this.handleViewportSize();

      // Clean up transition timeout
      if (this.imageTransitionTimeout) {
        clearTimeout(this.imageTransitionTimeout);
        this.imageTransitionTimeout = null;
      }

      document.removeEventListener('theme:resize:width', this.debouncedResize);
      emitter.off('replace-variant-image', this.handleReplaceVariantImage);
      emitter.off('upsell-images-updated', this.handleUpsellImagesUpdated);
      emitter.off('pdp-color-updated', this.handleColorUpdate);
      emitter.off('variant-updated', this.handleMiniPdpVariantUpdated);

      this.handleReplaceVariantImage = null;
      this.handleUpsellImagesUpdated = null;
      this.handleColorUpdate = null;
      this.handleMiniPdpVariantUpdated = null;
    }
  };
</script>

<template>
  <!-- Desktop -->
  <template v-if="localMedia.length">
    <template v-if="!mini_pdp && !isMobile">
      <div v-for="(product, index) in getAllImagesWithToggle" :class="'product__media desktop-images' + (product.node.sources ? ' desktop-video-height' : '') + (getAllImagesWithToggle.length === 1 ? ' has-single-image' : '') + (index >= getCurrentImageLimit && !showAllImages ? ' pdp-gallery-hidden' : '')" :key="'unified-' + index + product.id">
        <img v-if="product.node.image" :src="setImageDesktopSize(product.node.image.url)" :srcset="getDesktopImageSrcset(product.node.image.url)" class="tw-block tw-overflow-hidden tw-object-cover tw-w-full tw-h-full" :alt="getMediaAlt(product)" @click="handleZoom(product.node.image.url)" :loading="index < desktopPreloadCount ? 'eager' : 'lazy'" :fetchpriority="index < desktopPreloadCount ? 'high' : 'auto'" decoding="async" />

        <PDPVideoPlayer v-if="product.node.sources" :sources="product.node.sources" :productID="product.node.id" :handleVideoSize="getDesktopImageHeights" />

        <div class="media__buttons__holder" v-if="dataZoomEnable == 'true' && product.node.image">
          <button class="media__button media__button--zoom" data-zoom-button @click="handleZoom(product.node.image.url)">
            <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-search" viewBox="0 0 16 16">
              <path d="M16 14.864 14.863 16l-4.24-4.241a6.406 6.406 0 0 1-4.048 1.392 6.61 6.61 0 0 1-4.65-1.925A6.493 6.493 0 0 1 .5 9.098 6.51 6.51 0 0 1 0 6.575a6.536 6.536 0 0 1 1.922-4.652A6.62 6.62 0 0 1 4.062.5 6.52 6.52 0 0 1 6.575 0 6.5 6.5 0 0 1 9.1.5c.8.332 1.51.806 2.129 1.423a6.454 6.454 0 0 1 1.436 2.13 6.54 6.54 0 0 1 .498 2.522c0 1.503-.468 2.853-1.4 4.048L16 14.864zM3.053 10.091c.973.972 2.147 1.461 3.522 1.461 1.378 0 2.551-.489 3.525-1.461.968-.967 1.45-2.138 1.45-3.514 0-1.37-.482-2.545-1.45-3.524-.981-.968-2.154-1.45-3.525-1.45-1.376 0-2.547.483-3.513 1.45-.973.973-1.46 2.146-1.46 3.523 0 1.375.483 2.548 1.45 3.515z" />
            </svg>
            <span class="visually-hidden">Zoom in image {{ index + 1 }} of {{ getAllImagesWithToggle.length }}</span>
          </button>
        </div>
      </div>

      <!-- Toggle Button positioned at the end -->
      <div class="pdp-gallery-toggle-container" v-if="shouldShowExpandButton">
        <button
          class="pdp-gallery-toggle gt-p__cta"
          @click="toggleImageExpansion"
          :style="{
            '--expand-collapse-btn-line-offset': expandCollapseBtnLineOffset + 'px',
            '--expand-collapse-btn-margin-top': expandCollapseBtnMarginTop + 'px'
          }"
        >
          {{ showAllImages ? collapseText : expandText }}
        </button>
      </div>
    </template>
  </template>

  <!-- Mobile Freescroll -->
  <Teleport to="#pdp-freescroll-target" :disabled="!teleportTarget">
    <PDPFreescroll v-if="(localMedia.length > 1 && isMobile) || (localMedia.length && mini_pdp === 'true')" :key="carouselKey" :media="mediaToDisplay" :isMobile="isMobile" :zoomEnabled="dataZoomEnable === 'true'" :mobileImageWidth="pdp_mobile_image_width" :mobileMaxHeight="pdp_mobile_max_height" :itemsToShow="pdp_items_mobile" :showArrows="shouldShowArrows" :showPagination="show_pagination === 'true'" :imagePadding="zoom_gutter_mobile" :handleVideoSize="getDesktopImageHeights" :handleImageSize="getMobileImageHeights" :useCustomArrows="arrow_custom_on === 'true'" :isMiniPdp="mini_pdp === 'true'" :arrowLeftImage="arrow_left_image" :arrowLeftImageAlt="arrow_left_image_alt" :arrowLeftImageWidth="arrow_left_image_width" :arrowLeftImageHeight="arrow_left_image_height" :arrowLeftImageDisabled="arrow_left_image_disabled" :arrowLeftImageDisabledAlt="arrow_left_image_disabled_alt" :arrowLeftImageDisabledWidth="arrow_left_image_disabled_width" :arrowLeftImageDisabledHeight="arrow_left_image_disabled_height" @zoom="handleZoom" @slide-change="handleSlideChange" @carousel-ready="handleCarouselInit" />
    <div v-if="(localImages.length === 1 || (previousImages.length === 1 && !newImagesReady)) && isMobile && mini_pdp !== 'true'" style="width: 100%; overflow-x: hidden">
      <div class="product__media mobile-images" v-for="(product, index) in newImagesReady || localImages.length > 0 ? localImages : previousImages" :key="'mobile-' + index + (product.node?.id || index)">
        <img v-if="getMobileSingleSlideImageUrl(product)" :src="setImageMobileSize(getMobileSingleSlideImageUrl(product))" :srcset="getMobileImageSrcset(getMobileSingleSlideImageUrl(product))" loading="eager" fetchpriority="high" decoding="async" class="tw-block tw-overflow-hidden tw-object-cover tw-w-full" :width="product.node.image?.width || product.node.width" :height="product.node.image?.height || product.node.height" :alt="getMediaAlt(product)" />
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss">
  #PDPImages.is-mobile .desktop-images {
    display: none;
  }

  #PDPImages.pdp-single-image-mobile.is-mobile {
    overflow-x: hidden;
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    box-sizing: border-box;
    max-width: 100vw;
    --IMAGE-SPACING: 0px; /* neutralize theme spacing that applies negative margins on mobile */
  }
  #PDPImages.pdp-single-image-mobile.is-mobile .product__media.mobile-images {
    margin: 0;
    padding: 0;
    width: 100%;
    max-width: 100%;
  }
  #PDPImages.pdp-single-image-mobile.is-mobile .product__media.mobile-images img {
    display: block;
    width: 100%;
    max-width: 100%;
  }

  /* On desktop/tablet, force grid columns via CSS var; overrides theme.css widths */
  #PDPImages.pdp-gallery-grid-override {
    display: flex;
    flex-wrap: wrap;
    -moz-column-gap: var(--IMAGE-SPACING);
    column-gap: var(--IMAGE-SPACING);
    row-gap: var(--IMAGE-SPACING);
  }
  #PDPImages.pdp-gallery-grid-override.is-desktop {
    #PDPImages.pdp-gallery-grid-override .product__media {
      width: calc((100% - (var(--PDP-GALLERY-COLUMNS, var(--PDP-GALLERY-COLUMNS-DESKTOP)) - 1) * var(--IMAGE-SPACING)) / var(--PDP-GALLERY-COLUMNS, var(--PDP-GALLERY-COLUMNS-DESKTOP)));
    }
    /* Mosaic: make first item full width */
    #PDPImages.pdp-gallery-grid-override.product__grid--mosaic > .product__media:first-child {
      width: 100%;
    }
  }
  #PDPImages.pdp-gallery-grid-override.is-tablet {
    #PDPImages.pdp-gallery-grid-override .product__media {
      width: calc((100% - (var(--PDP-GALLERY-COLUMNS, var(--PDP-GALLERY-COLUMNS-TABLET)) - 1) * var(--IMAGE-SPACING)) / var(--PDP-GALLERY-COLUMNS, var(--PDP-GALLERY-COLUMNS-TABLET)));
    }
    /* Mosaic: make first item full width */
    #PDPImages.pdp-gallery-grid-override.product__grid--mosaic > .product__media:first-child {
      width: 100%;
    }
  }

  /* Hidden state for collapsed items when not showing all images */
  .product__media.pdp-gallery-hidden {
    opacity: 0;
    max-height: 0;
    overflow: hidden;
    margin: 0;
    padding: 0;
    flex-basis: 0;
    width: 0;
    min-width: 0;
  }

  .pdp-gallery-toggle-container {
    text-align: center;
    width: 100%;
    flex: 1 1 100%;
    flex-basis: 100%;
    order: 999; /* Ensure toggle button always appears at the end */
  }

  .pdp-gallery-toggle {
    margin-top: var(--expand-collapse-btn-margin-top, 1rem);
    transition: all 0.2s ease;
    text-decoration: underline;
    text-underline-offset: var(--expand-collapse-btn-line-offset, auto);

    &:hover {
      transform: translateY(-1px);
    }

    &:focus {
      outline: 2px solid var(--accent);
      outline-offset: 2px;
    }
  }
</style>
